import { supabase } from '../supabase'

/**
 * Claude-powered generations for Phone Pass Play.
 *
 * Pass Play is local-first: the state machines pre-fill every AI seat
 * from the offline persona templates so the game NEVER waits on the
 * network. When the device is online, these helpers ask the
 * `passplay-ai` edge function (Claude Haiku, same model as live rooms)
 * for a fresher line and the app swaps it in before it's shown.
 *
 * Every helper resolves to `null` on ANY problem — offline, signed-out,
 * rate-limited, model error, timeout — and the caller just keeps the
 * template text. No error surfaces to the player.
 */

const TIMEOUT_MS = 9000

function online(): boolean {
  return typeof navigator === 'undefined' || navigator.onLine !== false
}

async function invokePassplayAi(body: Record<string, unknown>): Promise<Record<string, unknown> | null> {
  if (!online()) return null
  try {
    const call = supabase.functions.invoke('passplay-ai', { body })
    const timeout = new Promise<null>(resolve => setTimeout(() => resolve(null), TIMEOUT_MS))
    const result = await Promise.race([call, timeout])
    if (!result || result.error || !result.data) return null
    return result.data as Record<string, unknown>
  } catch {
    return null
  }
}

/** One IET course description in-persona using exactly these cards. */
export async function generatePassplayCourse(opts: {
  personaName: string
  aiPlayerId?: string
  course: 1 | 2 | 3
  cardNames: string[]
}): Promise<string | null> {
  const data = await invokePassplayAi({
    game: 'ideatthat',
    kind: 'course',
    persona_name: opts.personaName,
    ai_player_id: opts.aiPlayerId ?? null,
    course: opts.course,
    card_names: opts.cardNames,
  })
  const text = typeof data?.text === 'string' ? data.text.trim() : ''
  return text.length > 0 ? text : null
}

/** Three Step phase-1: three short action phrases in-persona. */
export async function generatePassplayActions(opts: {
  personaName: string
  aiPlayerId?: string
}): Promise<[string, string, string] | null> {
  const data = await invokePassplayAi({
    game: 'three_step',
    kind: 'actions',
    persona_name: opts.personaName,
    ai_player_id: opts.aiPlayerId ?? null,
  })
  return threeStrings(data?.items)
}

/** Three Step: three steps answering the round prompt in-persona. */
export async function generatePassplaySteps(opts: {
  personaName: string
  aiPlayerId?: string
  actionA: string
  actionB: string
}): Promise<[string, string, string] | null> {
  const data = await invokePassplayAi({
    game: 'three_step',
    kind: 'steps',
    persona_name: opts.personaName,
    ai_player_id: opts.aiPlayerId ?? null,
    action_a: opts.actionA,
    action_b: opts.actionB,
  })
  return threeStrings(data?.items)
}

function threeStrings(v: unknown): [string, string, string] | null {
  if (!Array.isArray(v) || v.length < 3) return null
  const [a, b, c] = v.map(x => String(x ?? '').trim())
  if (!a || !b || !c) return null
  return [a, b, c]
}
